import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export const EMPTY_GUID_VALUE = "00000000-0000-0000-0000-000000000000" as const;
export type EmptyGuid = typeof EMPTY_GUID_VALUE;

export type GuidParams = z.infer<typeof GuidSchema.GuidParamsZ>;
export type GuidJSON = z.infer<typeof GuidSchema.GuidJSONZ>;

export default class GuidSchema {
  public static readonly type = z.custom<Guid>();

  public static readonly GuidParamsZ = z
    .uuid()
    .refine((val) => val !== "00000000-0000-0000-0000-000000000000", "Nil UUID is not allowed");
  public static readonly GuidJSONZ = z.uuid();

  public static validate(values: unknown, allowEmpty: boolean = false): GuidParams {
    const schema = allowEmpty
      ? z.union([z.uuid(), z.literal("00000000-0000-0000-0000-000000000000")])
      : GuidSchema.GuidParamsZ;
    return parseOrThrow(schema, values, {
      logError: true,
      schemaName: "GuidSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown, allowEmpty: boolean = false): string {
    const schema = allowEmpty
      ? z.union([z.uuid(), z.literal("00000000-0000-0000-0000-000000000000")])
      : GuidSchema.GuidJSONZ;
    return parseOrThrow(schema, json, {
      logError: true,
      schemaName: "GuidSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static serialize(guidValue: string, allowEmpty: boolean = false): string {
    const schema = allowEmpty
      ? z.union([z.uuid(), z.literal("00000000-0000-0000-0000-000000000000")])
      : GuidSchema.GuidJSONZ;
    return parseOrThrow(schema, guidValue, {
      logError: true,
      schemaName: "GuidSchema",
      operation: "serialize",
      payloadSample: guidValue,
    });
  }

  public static deserialize(data: unknown, allowEmpty: boolean = false): GuidParams {
    const schema = allowEmpty
      ? z.union([z.uuid(), z.literal("00000000-0000-0000-0000-000000000000")])
      : GuidSchema.GuidParamsZ;
    return parseOrThrow(schema, data, {
      logError: true,
      schemaName: "GuidSchema",
      operation: "deserialize",
      payloadSample: data,
    });
  }

  public static deserializeArray(data: unknown, allowEmpty: boolean = false): GuidParams[] {
    const schema = allowEmpty
      ? z.array(z.union([z.uuid(), z.literal("00000000-0000-0000-0000-000000000000")])).nullable()
      : z.array(GuidSchema.GuidParamsZ).nullable();
    return (
      parseOrThrow(schema, data, {
        logError: true,
        schemaName: "GuidSchema",
        operation: "deserializeArray",
        payloadSample: data,
      }) ?? []
    );
  }
}
